import { inject } from '@angular/core';
import { SessionService } from '@monsido/core/session/session.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { CoreDomainService } from '@client/app/core/services/domain/core-domain.service';
import { OauthService } from '@monsido/oauth/oauth.service';
import { LoginService } from '@client/app/core/services/login/login.service';
import { StateService, TransitionService, UrlService } from '@uirouter/core';
import { ErrorHandler } from '@client/app/core/services/error-handler/error-handler';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { RollbarErrorHandlerService } from '@client/ng2/external/rollbar/rollbar-error-handler.service';
import numeral from 'numeral';
import { BiService } from '@client/app/modules/business-intelligence/services/bi.service';


function clean (obj) {
    const tempObj = Object.assign({}, obj);
    for (const propName in tempObj) {
        if (!Object.prototype.hasOwnProperty.call(tempObj, propName)) {
            continue;
        }
        if (tempObj[propName] === null || tempObj[propName] === undefined) {
            delete tempObj[propName];
        }
    }
    return tempObj;
}

export function uiRouterHooks (
) {
    const ng2SessionService = inject(SessionService);
    const $state = inject(StateService);
    const urlService = inject(UrlService);
    const $transitions = inject(TransitionService);
    const ng2ErrorHandler = inject(ErrorHandler);
    const oauthService = inject(OauthService);
    const ng2CoreDomainService = inject(CoreDomainService);
    const ng2CoreLoginService = inject(LoginService);
    const gettextCatalog = inject(TranslateService);
    const ng2RollbarErrorHandlerService = inject(RollbarErrorHandlerService);
    const ng2MonEventsService = inject(MonEventService);
    const ng2BiService = inject(BiService);

    const getFeatureRedirectTarget = (transition) => {
        const targetPath = transition.targetState().$state().path;
        const allRequiredFeatures = new Set<string>();

        targetPath.forEach((state: any) => {
            if (state.data?.features && Array.isArray(state.data.features)) {
                state.data.features.forEach((feature: string) => allRequiredFeatures.add(feature));
            }
        });

        if (allRequiredFeatures.size === 0 || !ng2SessionService.domain) {
            return null;
        }

        const domainFeatures = ng2SessionService.domain.features;
        const hasAllFeatures = Boolean(domainFeatures) && Array.from(allRequiredFeatures).every(
            (feature: string) => domainFeatures[feature],
        );

        if (hasAllFeatures) {
            return null;
        }

        const targetParams = transition.targetState().params();
        if (targetParams.domainId !== undefined && targetParams.domainId !== null) {
            return $state.target('base.customer.domain.dashboard', targetParams);
        }

        return $state.target('base.customer.dashboard', targetParams);
    };

    const loadCurrentUser = (transition) => {
        return ng2CoreLoginService.getUser().then((user) => {
            ng2SessionService.setMe(user);
            if ('login.login' === transition.to().name) {
                return $state.target('base.dashboard');
            } else {
                ng2CoreDomainService.setupDomainGroupAfterReload();
                return getFeatureRedirectTarget(transition) || true;
            }
        }, ng2ErrorHandler.standardMsg);
    };

    const stateTitleMap = () => {
        return {
            'base.customer.admin.auditlog': gettextCatalog.getString('Acquia Web Governance - Audit Log'),
            'base.customer.admin.users': gettextCatalog.getString('Acquia Web Governance - User List'),
            'base.customer.admin.labels': gettextCatalog.getString('Acquia Web Governance - Labels'),
            'base.customer.admin.page_performance.dashboard': gettextCatalog.getString('Acquia Web Governance - Admin Page Performance Profiles'),
            'base.customer.dashboard': gettextCatalog.getString('Acquia Web Governance - Dashboard'),
            'base.customer.domain.page_performance.pages': gettextCatalog.getString('Acquia Web Governance - Page Performance Pages'),
            'base.customer.domain.qa.broken.links': gettextCatalog.getString('Acquia Web Governance - Broken Links'),
            'base.customer.domain.qa.broken.images': gettextCatalog.getString('Acquia Web Governance - Broken Images'),
            'base.customer.domain.qa.broken.pages': gettextCatalog.getString('Acquia Web Governance - Content With Broken Links'),
            'base.customer.domain.qa.pages': gettextCatalog.getString('Acquia Web Governance - Content With QA Errors'),
            'base.customer.domain.qa.summary': gettextCatalog.getString('Acquia Web Governance - Quality Assurance'),
            'base.customer.domain.seo.summary': gettextCatalog.getString('Acquia Web Governance - Search Engine Optimization'),
            'base.customer.domain.seo.issues': gettextCatalog.getString('Acquia Web Governance - SEO Checkpoints'),
            'base.customer.domain.seo.pages': gettextCatalog.getString('Acquia Web Governance - Pages With Opportunity'),
            'base.customer.domain.accessibility.summary': gettextCatalog.getString('Acquia Web Governance - Accessibility'),
            'base.customer.domain.accessibility.pages': gettextCatalog.getString('Acquia Web Governance - Pages With Failing Checks'),
            'base.customer.domain.accessibility.issues': gettextCatalog.getString('Acquia Web Governance - Accessibility Check List'),
            'base.customer.domain.accessibility.guidelines': gettextCatalog.getString('Acquia Web Governance - Accessibility Guidelines'),
            'base.customer.domain.accessibility.fasttrack': gettextCatalog.getString('Acquia Web Governance - Fast Track'),
            'base.customer.domain.accessibility.internal_pdf': gettextCatalog.getString('Acquia Web Governance - Internal PDF Compliance'),
            'base.customer.domain.accessibility.internal_pdf_accessibility': gettextCatalog.getString('Acquia Web Governance - Internal PDF Compliance'),
            'base.customer.domain.accessibility.external_pdf': gettextCatalog.getString('Acquia Web Governance - External PDF Compliance'),
            'base.customer.domain.accessibility.ignored_pages': gettextCatalog.getString('Acquia Web Governance - Pages With Ignored Checks'),
            'base.customer.heatmaps.dashboard': gettextCatalog.getString('Acquia Web Governance - Heatmaps'),
            'base.customer.domain.data_privacy': gettextCatalog.getString('Acquia Web Governance - Data Privacy'),
            'base.customer.domain.data_privacy.consent_overview': gettextCatalog.getString('Acquia Web Governance - Cookie Acceptance Rate'),
            'base.customer.domain.data_privacy.summary': gettextCatalog.getString('Acquia Web Governance - Data Privacy Summary'),
            'base.customer.domain.data_privacy.issues': gettextCatalog.getString('Acquia Web Governance - Data Privacy Issues'),
            'base.customer.domain.data_privacy.checklist': gettextCatalog.getString('Acquia Web Governance - Data Privacy Check List'),
            'base.customer.domain.data_privacy.domain-settings': gettextCatalog.getString('Acquia Web Governance - Data Privacy Guidelines'),
            'base.customer.domain.data_privacy.index': gettextCatalog.getString('Acquia Web Governance - Data Privacy Content'),
            'base.customer.domain.data_privacy.consent_overview.cookie_acceptance_rate': gettextCatalog.getString('Acquia Web Governance - Cookie Acceptance Rate'),
            'base.customer.domain.data_privacy.consent_overview.consent_log': gettextCatalog.getString('Acquia Web Governance - Cookie Consent Log'),
            'base.customer.domain.data_privacy.consent_overview.cookie_overview': gettextCatalog.getString('Acquia Web Governance - Cookie Overview'),
            'base.customer.domain.qa.readability.dashboard': gettextCatalog.getString('Acquia Web Governance - Readability Summary'),
            'base.customer.domain.qa.readability.checker': gettextCatalog.getString('Acquia Web Governance - Readability Checker'),
            'base.customer.domain.qa.spellcheck.dashboard': gettextCatalog.getString('Acquia Web Governance - Spellcheck Summary'),
            'base.customer.domain.qa.spellcheck.pages': gettextCatalog.getString('Acquia Web Governance - Pages With Misspellings'),
            'base.customer.domain.qa.spellcheck.confirmed': gettextCatalog.getString('Acquia Web Governance - Misspellings'),
            'base.customer.domain.qa.spellcheck.potential': gettextCatalog.getString('Acquia Web Governance - Potential Misspellings'),
            'base.customer.domain.qa.spellcheck.dictionary': gettextCatalog.getString('Acquia Web Governance - Account Dictionary'),
            'base.customer.domain.qa.spellcheck.ignored_misspellings': gettextCatalog.getString('Acquia Web Governance - Ignored Misspellings'),
            'base.customer.global.users': gettextCatalog.getString('Acquia Web Governance - User List'),
            'base.customer.domain.changesHistory': gettextCatalog.getString('Acquia Web Governance - Scan History'),
            'base.customer.domain.page_fix': gettextCatalog.getString('Acquia Web Governance - PageCorrect summary'),
            'base.customer.domain.page_fix.summary': gettextCatalog.getString('Acquia Web Governance - PageCorrect summary'),
            'base.customer.domain.page_fix.index': gettextCatalog.getString('Acquia Web Governance - PageCorrect list of fixes'),
            'base.customer.domain.page_fix.checks': gettextCatalog.getString('Acquia Web Governance - PageCorrect accessibility fixes'),
        };
    };

    // The following code should be kept
    // until all usages of numeraljs in the js-files
    // are removed
    ng2MonEventsService.addListener('numeraljsSetLocale' as any, (locale) => {
        // TODO:NOW Implement this
        numeral.locale(locale);
        // amMoment.changeLocale(locale);
    });

    $state.defaultErrorHandler(error => {
        // HACK: What is going on here?
        // Do nothing if the transition has been superseded by a different transition
        // Error type references in the following file
        // https://github.com/ui-router/core/blob/d461776/src/transition/rejectFactory.ts#L67
        if (error.type === 2) {
            return;
        }

        if (error.type === 6) {
            return;
        }
        // HACK: Why do we treat other transition rejections as warning only?

        // Treat other transition rejections as warning
        ng2RollbarErrorHandlerService.warn(error);
    });

    $transitions.onBefore({}, async function (transition) {
        // BI
        try {
            let ref: any;
            if ($state.href(transition.from().name || '')) {
                ref = $state.href(transition.from().name || '', {}, { absolute: true })?.split('?')[0];
            } else {
                ref = undefined;
            }
            const title = transition.to().data.title || 'Undefined';
            ng2BiService.pageView(
                $state.href(transition.to().name || '', {}, { absolute: true })?.split('?')[0],
                title,
                ref,
                false,
                clean(transition.params('to')),
            );
        } catch (e) {
        }

        if (!transition.to().data?.anonymous_access) {
            if (ng2CoreLoginService.isLoggedIn()) {
                if (!ng2SessionService.isUserLoaded()) {
                    return loadCurrentUser(transition);
                }
            } else {
                // HACK: Do we need this code related to issue overlay params?
                // All the params set from the url has 'undefined' values in transition.params.
                const {
                    issueOverlayIssueId,
                    issueOverlayIssueType,
                    issueOverlaySourceCodeId,
                    issueOverlayContentId,
                    issueOverlayPageId,
                    issueOverlayCheckId,
                    issueOverlayPageLinkId,
                } = transition.params();

                const issueOverlay = urlService.search().issueOverlay ? 'true' : null;
                const transParams = Object.assign(
                    Object.assign({}, transition.params('to')),
                    {
                        issueOverlay,
                        issueOverlayIssueId,
                        issueOverlayIssueType,
                        issueOverlaySourceCodeId,
                        issueOverlayContentId,
                        issueOverlayPageId,
                        issueOverlayCheckId,
                        issueOverlayPageLinkId,
                    },
                );

                const toState = transition.to().name;
                if (toState) {
                    oauthService.setRedirectState(
                        // HACK: Double check this casting
                        toState as any, Object.assign(transParams),
                    );
                }
                return $state.target('login.login');
            }
        }

        return getFeatureRedirectTarget(transition) || true;
    });

    $transitions.onSuccess({}, function (transition) {
        ng2MonEventsService.run('transitionsOnSuccess', {
            page: transition.to().name,
            title: transition.to().data.title || gettextCatalog.getString('Acquia Web Governance'),
        });

        // HACK: Why setTimeout
        setTimeout(() => {
            let newTitle = transition.to().data.title;
            const toStateName = transition.to().name || '';
            if (stateTitleMap()[toStateName]) {
                newTitle = stateTitleMap()[toStateName];
            }
            window.document.title = newTitle || gettextCatalog.getString('Acquia Web Governance');
        }, 0);

    });

    $transitions.onError({}, function () {
        return Promise.resolve();
    });
}
